import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import Wordmark from '../../components/Wordmark';
import ThemeToggle from '../../components/ThemeToggle';
import { useAuth } from '../../contexts/AuthContext';

type Tab = { label: string; href: string };

type Props = {
  role: 'member' | 'vendor' | 'platform';
  tabs: Tab[];
  children: ReactNode;
};

export default function DashboardShell({ role, tabs, children }: Props) {
  const { profile, signOut } = useAuth();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close mobile drawer whenever the route changes.
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [menuOpen]);

  const roleLabel = role === 'platform' ? 'Platform' : role === 'vendor' ? 'Vendor' : 'Your team';

  return (
    <div className="dash-root">
      {/* Mobile top bar */}
      <div className="dash-topbar show-mobile-flex">
        <Link to="/" aria-label="Goaround home"><Wordmark size={22} /></Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ThemeToggle />
          <button
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            style={{ width: 40, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--ink)', borderRadius: 999, background: 'transparent', cursor: 'pointer', color: 'var(--ink)' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
          </button>
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="dash-sidebar hide-mobile">
        <Link to="/" style={{ marginBottom: 32 }}>
          <Wordmark size={24} />
        </Link>
        <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 16 }}>
          {roleLabel}
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
          {tabs.map((t) => {
            const active = loc.pathname === t.href || (t.href !== '/dashboard' && loc.pathname.startsWith(t.href));
            return (
              <Link
                key={t.href}
                to={t.href}
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: active ? 'var(--ink)' : 'transparent',
                  color: active ? 'var(--bg)' : 'var(--ink)',
                  fontSize: 15,
                  fontWeight: 500,
                }}
              >
                {t.label}
              </Link>
            );
          })}
        </nav>
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 16, marginTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{profile?.full_name || profile?.email}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginBottom: 12 }}>{profile?.email}</div>
          <button onClick={signOut} className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center' }}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="dash-main">
        {/* Theme toggle (desktop only. Mobile has it in the topbar). */}
        <div className="hide-mobile" style={{ position: 'absolute', top: 24, right: 24, zIndex: 10 }}>
          <ThemeToggle />
        </div>
        {children}
      </main>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="drawer-backdrop">
          <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 0' }}>
            <Link to="/" onClick={() => setMenuOpen(false)} aria-label="Goaround home"><Wordmark size={22} /></Link>
            <button
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              style={{ width: 40, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--ink)', borderRadius: 999, background: 'transparent', cursor: 'pointer', color: 'var(--ink)', fontSize: 22 }}
            >×</button>
          </div>
          <div className="container" style={{ paddingTop: 24, paddingBottom: 24 }}>
            <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 12 }}>{roleLabel}</div>
            {tabs.map((t) => {
              const active = loc.pathname === t.href || (t.href !== '/dashboard' && loc.pathname.startsWith(t.href));
              return (
                <Link
                  key={t.href}
                  to={t.href}
                  onClick={() => setMenuOpen(false)}
                  className="drawer-link"
                  style={{ color: active ? 'var(--primary)' : 'var(--ink)' }}
                >
                  {t.label}
                </Link>
              );
            })}
          </div>
          <div className="container" style={{ padding: '24px 0 32px', borderTop: '1px solid var(--line)' }}>
            <div style={{ fontSize: 15, fontWeight: 500 }}>{profile?.full_name || profile?.email}</div>
            <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginBottom: 16 }}>{profile?.email}</div>
            <button
              onClick={async () => { setMenuOpen(false); await signOut(); }}
              className="btn btn-ghost"
              style={{ width: '100%', justifyContent: 'center' }}
            >Sign out</button>
          </div>
        </div>
      )}

      <style>{`
        .dash-root { min-height: 100vh; display: grid; grid-template-columns: 260px 1fr; }
        .dash-sidebar {
          border-right: 1px solid var(--line);
          padding: 28px;
          position: sticky;
          top: 0;
          height: 100vh;
          display: flex;
          flex-direction: column;
        }
        .dash-main { padding: 40px 48px 80px; min-width: 0; position: relative; }
        .dash-topbar {
          display: none;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--line);
          background: var(--bg);
          position: sticky;
          top: 0;
          z-index: 20;
          grid-column: 1 / -1;
        }
        @media (max-width: 860px) {
          .dash-root { grid-template-columns: 1fr; }
          .dash-main { padding: 24px 20px 64px; }
        }
      `}</style>
    </div>
  );
}
